import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Alert,
  Tabs,
  Tab,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Category, Question, Section } from '../FormBuilder';
import QuestionDialog from './QuestionDialog';

interface QuestionsStepProps {
  sections: Section[];
  categories: Category[];
  questions: Question[];
  onChange: (questions: Question[]) => void;
}

const QUESTION_TYPES = {
  yes_no: 'Sí / No / N/A',
  scale: 'Escala Numérica',
  text: 'Texto Libre',
  number: 'Número',
  select: 'Selección Única',
  multiple: 'Selección Múltiple',
  date: 'Fecha',
  time: 'Hora'
};

const QuestionsStep: React.FC<QuestionsStepProps> = ({ sections, categories, questions, onChange }) => {
  const [selectedSectionTab, setSelectedSectionTab] = useState<number>(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null); // ✅ Índice de la pregunta en el array

  console.log('QuestionsStep received:', { 
    sections: sections.length, 
    categories: categories.length, 
    questions: questions.length,
    questionsData: questions,
    firstQuestion: questions[0],
    sectionsData: sections,
    categoriesData: categories
  });

  // Debug: verificar section_id y category_id
  if (questions.length > 0) {
    console.log('First 3 questions detail:', questions.slice(0, 3).map(q => ({
      id: q.id,
      texto: q.texto,
      section_id: q.section_id,
      section_id_type: typeof q.section_id,
      category_id: q.category_id,
      orden: q.orden
    })));
    console.log('Section IDs:', sections.map(s => ({ id: s.id, type: typeof s.id, name: s.name })));
  }

  const handleOpenDialog = (question?: Question, questionIndex?: number) => {
    if (question !== undefined && questionIndex !== undefined) {
      setEditingQuestion(question);
      setEditingIndex(questionIndex); // ✅ Guardar índice
    } else {
      setEditingQuestion(null);
      setEditingIndex(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingQuestion(null);
    setEditingIndex(null); // ✅ Limpiar índice
  };

  const handleSaveQuestion = (question: Question) => {
    console.log('=== SAVING QUESTION ===');
    console.log('Mode:', editingQuestion ? 'EDIT' : 'CREATE');
    console.log('Editing index:', editingIndex);
    console.log('Question from dialog:', { 
      id: question.id, 
      section_id: question.section_id, 
      category_id: question.category_id,
      orden: question.orden,
      texto: question.texto
    });
    
    if (editingQuestion && editingIndex !== null) {
      // MODO EDICIÓN: Preservar section_id y orden originales
      console.log('Editing question at index:', editingIndex);
      console.log('Original question:', {
        id: editingQuestion.id,
        section_id: editingQuestion.section_id,
        category_id: editingQuestion.category_id,
        orden: editingQuestion.orden
      });
      
      const updatedQuestion = {
        ...question,
        section_id: editingQuestion.section_id, // ✅ Preservar sección original
        orden: editingQuestion.orden // ✅ Preservar orden original
      };
      
      console.log('Updated question:', { 
        id: updatedQuestion.id, 
        section_id: updatedQuestion.section_id, 
        category_id: updatedQuestion.category_id,
        orden: updatedQuestion.orden
      });
      
      // ✅ Usar ÍNDICE en lugar de ID para encontrar la pregunta
      const updatedQuestions = [...questions];
      updatedQuestions[editingIndex] = updatedQuestion;
      onChange(updatedQuestions);
    } else {
      // MODO CREACIÓN: Asignar sección del tab activo y nuevo orden
      const currentSection = sections[selectedSectionTab];
      if (!currentSection) return;
      
      console.log('Creating new question in section:', currentSection.id, currentSection.name);
      
      // ✅ FIX: Calcular orden basado en la CATEGORÍA, no en la sección
      // El constraint "unique_pregunta_orden" valida (category_id, order)
      const categoryQuestions = questions.filter(q => q.category_id === question.category_id);
      const newOrden = categoryQuestions.length + 1;
      
      console.log('📊 Calculating order for new question:', {
        category_id: question.category_id,
        existing_questions_in_category: categoryQuestions.length,
        new_order: newOrden
      });
      
      const newQuestion: Question = {
        ...question,
        section_id: currentSection.id!, // ✅ Asignar sección del tab activo
        orden: newOrden
      };
      
      console.log('New question:', { 
        section_id: newQuestion.section_id, 
        category_id: newQuestion.category_id,
        orden: newQuestion.orden,
        texto: newQuestion.texto
      });
      
      onChange([...questions, newQuestion]);
    }
    
    handleCloseDialog();
  };

  const handleDeleteQuestion = (questionId: number | undefined) => {
    if (!questionId) return;
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    onChange(updatedQuestions);
  };

  const handleMoveQuestion = (sectionId: number, questionIndex: number, direction: 'up' | 'down') => {
    const sectionQuestions = getQuestionsBySection(sectionId);
    
    if (
      (direction === 'up' && questionIndex === 0) ||
      (direction === 'down' && questionIndex === sectionQuestions.length - 1)
    ) {
      return;
    }

    const newSectionQuestions = [...sectionQuestions];
    const targetIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
    
    [newSectionQuestions[questionIndex], newSectionQuestions[targetIndex]] = 
    [newSectionQuestions[targetIndex], newSectionQuestions[questionIndex]];
    
    const reorderedQuestions = newSectionQuestions.map((q, idx) => ({
      ...q,
      orden: idx + 1
    }));
    
    const otherQuestions = questions.filter(q => q.section_id !== sectionId);
    const allQuestions = [...otherQuestions, ...reorderedQuestions];
    
    onChange(allQuestions);
  };

  const getQuestionsBySection = (sectionId: number): Question[] => {
    const filtered = questions.filter(q => q.section_id === sectionId);
    console.log(`getQuestionsBySection(${sectionId}):`, {
      total: questions.length,
      filtered: filtered.length,
      sectionId,
      sample: filtered[0] ? { id: filtered[0].id, section_id: filtered[0].section_id } : 'none'
    });
    return filtered.sort((a, b) => a.orden - b.orden);
  };

  const getCategoryById = (categoryId: number | undefined): Category | undefined => {
    return categories.find(c => c.id === categoryId);
  };

  const calculateCategoryPonderacion = (categoryId: number): number => {
    const categoryQuestions = questions.filter(q => q.category_id === categoryId);
    return categoryQuestions.reduce((sum, q) => sum + q.ponderacion, 0);
  };

  if (sections.length === 0) {
    return (
      <Alert severity="warning">
        Debe crear al menos una sección antes de agregar preguntas.
      </Alert>
    );
  }

  if (categories.length === 0) {
    return (
      <Alert severity="warning">
        Debe crear al menos una categoría antes de agregar preguntas.
      </Alert>
    );
  }

  const currentSection = sections[selectedSectionTab];
  const currentSectionQuestions = currentSection ? getQuestionsBySection(currentSection.id!) : [];

  const sortedCategories = [...categories].sort((a, b) => 
    a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">
            Preguntas del Formulario
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Navegue por secciones y agregue preguntas. Cada pregunta pertenece a una sección (navegación) y una categoría (análisis).
          </Typography>
        </Box>
      </Box>

      {/* Resumen de ponderaciones por categoría */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" gutterBottom>
          Resumen de Ponderaciones por Categoría
        </Typography>
        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
          Cada categoría debe sumar 100% de ponderación total
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {sortedCategories.map(category => {
            const ponderacion = calculateCategoryPonderacion(category.id!);
            const isValid = Math.abs(ponderacion - 100) < 0.01;
            const hasQuestions = questions.filter(q => q.category_id === category.id).length > 0;
            
            return (
              <Chip
                key={category.id}
                label={`${category.nombre}: ${ponderacion.toFixed(1)}%`}
                color={isValid && hasQuestions ? 'success' : hasQuestions ? 'warning' : 'default'}
                size="small"
                icon={
                  <Box
                    component="span"
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: category.color || '#2196F3',
                      borderRadius: '50%',
                      display: 'inline-block',
                      ml: 1
                    }}
                  />
                }
              />
            );
          })}
        </Box>
        {sortedCategories.some(cat => {
          const p = calculateCategoryPonderacion(cat.id!);
          const hasQ = questions.filter(q => q.category_id === cat.id).length > 0;
          return hasQ && Math.abs(p - 100) > 0.01;
        }) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Algunas categorías no suman 100% de ponderación. Ajuste los valores antes de continuar.
          </Alert>
        )}
      </Paper>

      {/* Tabs por sección */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={selectedSectionTab}
          onChange={(_, newValue) => setSelectedSectionTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {sections.map((section, index) => (
            <Tab
              key={section.id || index}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    component="span"
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: section.color || '#2196F3',
                      borderRadius: '50%',
                      display: 'inline-block'
                    }}
                  />
                  <span>{section.name}</span>
                  <Chip
                    label={getQuestionsBySection(section.id!).length}
                    size="small"
                    sx={{ ml: 0.5 }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Contenido de la sección actual */}
      {currentSection && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="subtitle1">
                {currentSection.name}
              </Typography>
              {currentSection.description && (
                <Typography variant="body2" color="textSecondary">
                  {currentSection.description}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Agregar Pregunta
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {currentSectionQuestions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                No hay preguntas en esta sección
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Agregar Primera Pregunta
              </Button>
            </Paper>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {currentSectionQuestions.map((question, localIndex) => {
                const category = getCategoryById(question.category_id);
                // ✅ Encontrar el índice GLOBAL usando referencia de objeto
                // IMPORTANTE: Usar indexOf para comparación por referencia, no por contenido
                const globalIndex = questions.indexOf(question);
                
                return (
                  <Paper
                    key={question.id || localIndex}
                    sx={{
                      p: 2,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ minWidth: 30 }}
                      >
                        {localIndex + 1}.
                      </Typography>
                      
                      <Box flex={1}>
                        <Typography variant="body1" gutterBottom>
                          {question.texto}
                        </Typography>
                        
                        {question.descripcion && (
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {question.descripcion}
                          </Typography>
                        )}
                        
                        <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                          <Chip
                            label={QUESTION_TYPES[question.tipo]}
                            size="small"
                            variant="outlined"
                          />
                          {category && (
                            <Chip
                              label={`Categoría: ${category.nombre}`}
                              size="small"
                              sx={{
                                bgcolor: category.color || '#2196F3',
                                color: 'white'
                              }}
                            />
                          )}
                          <Chip
                            label={`${question.ponderacion}%`}
                            size="small"
                            color="primary"
                          />
                          {question.obligatoria && (
                            <Chip
                              label="Obligatoria"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                          {question.permite_evidencia && (
                            <Chip label="📷 Evidencia" size="small" />
                          )}
                          {question.permite_comentario && (
                            <Chip label="💬 Comentarios" size="small" />
                          )}
                        </Box>
                      </Box>
                      
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Mover arriba">
                          <span>
                            <IconButton
                              size="small"
                              disabled={localIndex === 0}
                              onClick={() => handleMoveQuestion(currentSection.id!, localIndex, 'up')}
                            >
                              ↑
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Mover abajo">
                          <span>
                            <IconButton
                              size="small"
                              disabled={localIndex === currentSectionQuestions.length - 1}
                              onClick={() => handleMoveQuestion(currentSection.id!, localIndex, 'down')}
                            >
                              ↓
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(question, globalIndex)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteQuestion(question.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Dialog para crear/editar pregunta */}
      {currentSection && (
        <QuestionDialog
          open={dialogOpen}
          question={editingQuestion}
          sectionId={currentSection.id!}
          categories={categories}
          onClose={handleCloseDialog}
          onSave={handleSaveQuestion}
        />
      )}
    </Box>
  );
};

export default QuestionsStep;
